import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { ArrowLeft, Plane, Save } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'PENDING_REVIEW', label: 'En attente' },
  { value: 'ELIGIBLE', label: 'Éligible' },
  { value: 'IN_PROGRESS', label: 'En cours' },
  { value: 'NEGOTIATING', label: 'Négociation' },
  { value: 'WON', label: 'Gagné' },
  { value: 'PAID', label: 'Payé' },
  { value: 'INELIGIBLE', label: 'Non éligible' },
  { value: 'LOST', label: 'Perdu' },
];

const DELAY_REASONS = [
  { value: 'delay', label: 'Retard' },
  { value: 'cancellation', label: 'Annulation' },
  { value: 'denied_boarding', label: "Refus d'embarquement" },
];

export default function NewCase() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    clientPhone: '',
    clientName: '',
    flightNumber: '',
    flightDate: '',
    departureAirport: '',
    arrivalAirport: '',
    airline: '',
    delayReason: 'delay',
    delayHours: '',
    passengerCount: 1,
    bookingReference: '',
    initialStatus: 'PENDING_REVIEW',
    notifyClient: false,
  });

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.clientPhone.trim()) {
      setError('Le numéro WhatsApp du client est obligatoire');
      return;
    }

    setSaving(true);
    try {
      const newCase = await api.createCase({
        ...form,
        clientPhone: form.clientPhone.replace(/[^\d]/g, ''), // strip spaces, +, etc.
        passengerCount: parseInt(form.passengerCount) || 1,
        delayHours: form.delayHours ? parseFloat(form.delayHours) : null,
      });
      navigate(`/cases/${newCase.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création du dossier');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <Link to="/cases" style={styles.back}><ArrowLeft size={16} /> Retour aux dossiers</Link>

      <div style={styles.header}>
        <div style={styles.iconWrap}><Plane size={22} color="#fff" /></div>
        <div>
          <h1 style={styles.title}>Nouveau dossier</h1>
          <p style={styles.subtitle}>Saisie manuelle (appel, email, en personne...)</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <Section title="Client">
          <Row>
            <Field label="Numéro WhatsApp *" required>
              <input
                type="tel"
                placeholder="33612345678"
                value={form.clientPhone}
                onChange={set('clientPhone')}
                style={styles.input}
                required
              />
            </Field>
            <Field label="Nom complet">
              <input
                type="text"
                placeholder="Prénom Nom"
                value={form.clientName}
                onChange={set('clientName')}
                style={styles.input}
              />
            </Field>
          </Row>
        </Section>

        <Section title="Vol">
          <Row>
            <Field label="Numéro de vol">
              <input
                type="text"
                placeholder="AF702"
                value={form.flightNumber}
                onChange={set('flightNumber')}
                style={styles.input}
              />
            </Field>
            <Field label="Date du vol">
              <input
                type="date"
                value={form.flightDate}
                onChange={set('flightDate')}
                style={styles.input}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Aéroport départ">
              <input
                type="text"
                placeholder="CDG"
                value={form.departureAirport}
                onChange={set('departureAirport')}
                style={styles.input}
                maxLength={4}
              />
            </Field>
            <Field label="Aéroport arrivée">
              <input
                type="text"
                placeholder="OUA"
                value={form.arrivalAirport}
                onChange={set('arrivalAirport')}
                style={styles.input}
                maxLength={4}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Compagnie aérienne">
              <input
                type="text"
                placeholder="Air France"
                value={form.airline}
                onChange={set('airline')}
                style={styles.input}
              />
            </Field>
            <Field label="Référence réservation (PNR)">
              <input
                type="text"
                placeholder="XHT5K2"
                value={form.bookingReference}
                onChange={set('bookingReference')}
                style={styles.input}
              />
            </Field>
          </Row>
        </Section>

        <Section title="Incident">
          <Row>
            <Field label="Type de problème">
              <select value={form.delayReason} onChange={set('delayReason')} style={styles.input}>
                {DELAY_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Durée du retard (heures)">
              <input
                type="number"
                step="0.5"
                placeholder="4"
                value={form.delayHours}
                onChange={set('delayHours')}
                style={styles.input}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Nombre de passagers">
              <input
                type="number"
                min="1"
                value={form.passengerCount}
                onChange={set('passengerCount')}
                style={styles.input}
              />
            </Field>
            <Field label="Statut initial">
              <select value={form.initialStatus} onChange={set('initialStatus')} style={styles.input}>
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </Row>
        </Section>

        <label style={styles.checkboxRow}>
          <input type="checkbox" checked={form.notifyClient} onChange={set('notifyClient')} />
          <span>Envoyer un message WhatsApp de confirmation au client</span>
        </label>

        {error && <p style={styles.error}>{error}</p>}

        <button type="submit" disabled={saving} style={styles.submitBtn}>
          <Save size={16} /> {saving ? 'Création...' : 'Créer le dossier'}
        </button>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={styles.row}>{children}</div>;
}

function Field({ label, children, required }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 720, margin: '0 auto' },
  back: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#666', textDecoration: 'none', fontSize: 14, marginBottom: 16, fontWeight: 600 },
  header: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 },
  iconWrap: { width: 44, height: 44, borderRadius: 12, background: '#0D1B3E', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: 22, fontWeight: 800, color: '#0D1B3E', margin: 0 },
  subtitle: { fontSize: 13, color: '#888', margin: '2px 0 0' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  section: { background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: 13, fontWeight: 800, color: '#0D1B3E', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: 0.5 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12.5, fontWeight: 700, color: '#666' },
  input: { padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#444', background: '#fff', padding: 16, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  error: { color: '#DC2626', fontSize: 13, margin: 0 },
  submitBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#E8920A', color: '#fff', border: 'none', borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};
